import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Skeleton } from "@mui/material";
import Plus from "assets/img/svg/plus";
import { formatVatType } from "components/Beneficiar/utils";
import DeleteModal from "components/shared/DeleteModal";
import IosSwitch from "components/shared/IosSwitch";
import ModalBase from "components/shared/ModalBase";
import SettingsHeader from "components/shared/SettingsHeader";
import StyledButton from "components/shared/StyledButton";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { findCountry, getCountries } from "countries";
import { EXPIRING_SOON_DAYS } from "helpers/beneficiaryDocuments";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type {
    IBeneficiaryDocumentType,
    TDocumentTypeCriteria,
    TDocumentTypePayload,
} from "store/beneficiaryDocumentTypesStore";
import {
    createDocumentType,
    deleteDocumentType,
    fetchDocumentTypes,
    updateDocumentType,
} from "store/beneficiaryDocumentTypesStore";
import { useAppDispatch, useAppSelector } from "store/store";

import DocumentTypeForm from "./DocumentTypeForm";
import styles from "./style.module.scss";

type ModalType = "" | "create" | "edit" | "delete";

/**
 * Settings > Beneficiary documents: the administrative documents expected from
 * beneficiaries (type, validity requirement, applicability on the tax profile).
 */
const BeneficiaryDocumentsSettings = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { documentTypes, status, saveStatus } = useAppSelector((state) => state.beneficiaryDocumentTypes);
    const [modal, setModal] = useState<ModalType>("");
    const [chosen, setChosen] = useState<IBeneficiaryDocumentType | null>(null);
    const countries = useMemo(() => getCountries(), []);
    const { countryFeatures } = useCompanySettings();

    useEffect(() => {
        dispatch(fetchDocumentTypes());
    }, []);

    /** Every selected criterion in words, with the labels of the beneficiary form. */
    const describeCriteria = (criteria: TDocumentTypeCriteria) => {
        const parts: string[] = [];
        if (criteria.precount !== null) {
            const option = countryFeatures.precount.options.find((o) => o.value === criteria.precount);
            parts.push(
                `${t("BeneficiaryDocuments.Criteria.Precount")} : ${option ? t(option.label) : criteria.precount}`,
            );
        }
        if (criteria.vatProfiles?.length) {
            parts.push(criteria.vatProfiles.map((vat) => formatVatType(vat)).join(", "));
        }
        if (criteria.entity) {
            parts.push(t(criteria.entity === "legal" ? "LegalEntity.Legal" : "LegalEntity.Physical"));
        }
        if (criteria.countries?.length) {
            const names = criteria.countries.map((code) => findCountry(countries, code)?.name ?? code).join(", ");
            parts.push(
                criteria.excludeCountries ? t("BeneficiaryDocuments.Criteria.Abroad", { country: names }) : names,
            );
        }
        if (criteria.socialProfileTypes?.length) {
            // Worded as in the form: the social profiles are the precount field of social-profile companies
            const label = t(
                countryFeatures.precountMode === "direct"
                    ? "BeneficiaryDocuments.Criteria.SocialProfile"
                    : "BeneficiaryDocuments.Criteria.Precount",
            );
            parts.push(`${label} : ${criteria.socialProfileTypes.join(", ")}`);
        }

        return parts.length ? parts.join(" · ") : t("BeneficiaryDocuments.Criteria.AllBeneficiaries");
    };

    const handleSubmit = async (data: TDocumentTypePayload) => {
        try {
            if (chosen) await dispatch(updateDocumentType({ id: chosen.id, data })).unwrap();
            else await dispatch(createDocumentType(data)).unwrap();
            setModal("");
        } catch {
            toast.error(t("BeneficiaryDocuments.SaveError") as string);
        }
    };

    const handleDelete = async () => {
        if (!chosen) return;
        try {
            await dispatch(deleteDocumentType(chosen.id)).unwrap();
        } catch (error: any) {
            // 423: documents are attached → the type can only be deactivated
            toast.error(
                t(
                    error?.message === "DocumentTypeInUse"
                        ? "BeneficiaryDocuments.DeleteInUse"
                        : "BeneficiaryDocuments.SaveError",
                ) as string,
            );
        }
        setModal("");
    };

    const toggleActive = (type: IBeneficiaryDocumentType, isActive: boolean) =>
        dispatch(updateDocumentType({ id: type.id, data: { isActive } }));

    const openEdit = (type: IBeneficiaryDocumentType) => {
        setChosen(type);
        setModal("edit");
    };

    const activeCount = documentTypes.filter((type) => type.isActive).length;

    return (
        <div className={styles.page}>
            <SettingsHeader
                title={t("BeneficiaryDocuments.Title")}
                subtitle={t("BeneficiaryDocuments.Subtitle")}
                renderAction={() => (
                    <StyledButton
                        buttonType="primary"
                        onClick={() => {
                            setChosen(null);
                            setModal("create");
                        }}
                    >
                        <Plus />
                        {t("BeneficiaryDocuments.AddType")}
                    </StyledButton>
                )}
            />

            {status === "pending" && !documentTypes.length ? (
                <Skeleton variant="rectangular" height={120} />
            ) : documentTypes.length === 0 ? (
                <div className={styles.empty}>{t("BeneficiaryDocuments.NoType")}</div>
            ) : (
                <div className={styles.card}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>{t("BeneficiaryDocuments.Columns.Name")}</th>
                                <th>{t("BeneficiaryDocuments.Columns.Validity")}</th>
                                <th>{t("BeneficiaryDocuments.Columns.ExpectedFrom")}</th>
                                <th className={styles.center}>{t("BeneficiaryDocuments.Columns.Active")}</th>
                                <th className={styles.right}>
                                    <span className={styles.srOnly}>{t("BeneficiaryDocuments.Actions.Menu")}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {documentTypes.map((type) => (
                                <tr key={type.id}>
                                    <td>
                                        <button
                                            type="button"
                                            className={`${styles.nameButton} ${type.isActive ? "" : styles.inactive}`}
                                            onClick={() => openEdit(type)}
                                        >
                                            {type.name}
                                        </button>
                                    </td>
                                    <td className={styles.muted}>
                                        {type.requiresValidity
                                            ? t("BeneficiaryDocuments.ValidityRequired")
                                            : t("BeneficiaryDocuments.ValidityOptional")}
                                    </td>
                                    <td className={styles.muted}>{describeCriteria(type.criteria)}</td>
                                    <td className={styles.center}>
                                        <IosSwitch
                                            checked={type.isActive}
                                            onChange={(e) => toggleActive(type, e.target.checked)}
                                        />
                                    </td>
                                    <td className={styles.right}>
                                        <ThreeDotMenu
                                            style={{ border: "none", color: "#646464", width: 30, height: 30 }}
                                            menuIcon={<MoreVertIcon fontSize="small" />}
                                            options={[
                                                {
                                                    id: 1,
                                                    name: t("BeneficiaryDocuments.Actions.Edit"),
                                                    action: () => openEdit(type),
                                                },
                                                {
                                                    id: 2,
                                                    name: t("BeneficiaryDocuments.Actions.Delete"),
                                                    action: () => {
                                                        setChosen(type);
                                                        setModal("delete");
                                                    },
                                                },
                                            ]}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className={styles.summary}>
                        {t("BeneficiaryDocuments.Summary", { total: documentTypes.length, active: activeCount })}
                    </div>
                </div>
            )}

            {/* Fixed in v1: shown as in the mockup so the publisher knows how the statuses are computed */}
            <div className={styles.rules}>
                <div className={styles.rulesTitle}>{t("BeneficiaryDocuments.Rules.Title")}</div>
                <div>
                    <div className={styles.rule}>
                        <div className={styles.ruleText}>
                            <span>{t("BeneficiaryDocuments.Rules.ExpiringSoon")}</span>
                            <span>{t("BeneficiaryDocuments.Rules.ExpiringSoonHelp")}</span>
                        </div>
                        <div className={styles.ruleInput}>
                            <input
                                type="number"
                                value={EXPIRING_SOON_DAYS}
                                readOnly
                                aria-label={t("BeneficiaryDocuments.Rules.ExpiringSoon")}
                            />
                            <span>{t("BeneficiaryDocuments.Rules.Days")}</span>
                        </div>
                    </div>
                    <div className={styles.rule}>
                        <div className={styles.ruleText}>
                            <span>{t("BeneficiaryDocuments.Rules.Rejected")}</span>
                            <span>{t("BeneficiaryDocuments.Rules.RejectedHelp")}</span>
                        </div>
                        <IosSwitch checked readOnly className={styles.readOnlySwitch} />
                    </div>
                </div>
            </div>

            <ModalBase
                open={modal === "create" || modal === "edit"}
                onClose={() => setModal("")}
                modalTitle={chosen ? t("BeneficiaryDocuments.EditType") : t("BeneficiaryDocuments.AddType")}
                modalSubtitle={t("BeneficiaryDocuments.Form.Subtitle")}
            >
                <DocumentTypeForm
                    key={chosen?.id ?? "new"}
                    initial={chosen}
                    saving={saveStatus === "pending"}
                    onSubmit={handleSubmit}
                    onCancel={() => setModal("")}
                />
            </ModalBase>

            <DeleteModal
                open={modal === "delete"}
                onClose={() => setModal("")}
                onDelete={handleDelete}
                elementTitle={chosen?.name ?? ""}
                description={t("BeneficiaryDocuments.DeleteDescription")}
            />
        </div>
    );
};

export default BeneficiaryDocumentsSettings;
